/**
 * SummaryPanel — Updates the glass chip displays in the center column
 * and the weight/spacer/gas readouts in the Anpassen panel.
 * Also drives the price display using PricingEngine.
 */
export default class SummaryPanel {
  constructor(bridge) {
    this.bridge = bridge;

    this.glassDisplays = {
      frg:     document.getElementById('frg-selected-name'),
      middle:  document.getElementById('mg-selected-name'),
      outside: document.getElementById('og-selected-name'),
    };

    this.spacerDisplay = document.getElementById('spacer-display');
    this.gasDisplay = document.getElementById('gas-display');
    this.weightDisplay = document.getElementById('stat-weight');
    this.techSound = document.getElementById('tech-sound');
    this.priceDisplay = document.getElementById('stat-price');
    this.thicknessDisplay = document.getElementById('stat-thickness');

    // Mobile overlay elements
    this.mobileWeight = document.getElementById('mobile-stat-weight');
    this.mobileSound = document.getElementById('mobile-stat-sound');

    this._observeSound();
    this._observeGlassSelections();
  }

  _observeSound() {
    const soundEl = document.querySelector('.sound');
    if (!soundEl) return;

    const observer = new MutationObserver(() => {
      const soundText = soundEl.textContent || soundEl.innerHTML;
      const soundDisplay = document.getElementById('sound-display');
      if (soundDisplay) soundDisplay.textContent = soundText ? soundText + ' dB' : '— dB';
      // Sync to tech properties panel
      if (this.techSound) this.techSound.textContent = soundText || '— dB';
      // Sync to mobile overlay
      if (this.mobileSound) this.mobileSound.textContent = soundText || '—';
    });

    observer.observe(soundEl, { childList: true, characterData: true, subtree: true });
  }

  _observeGlassSelections() {
    const selectors = {
      frg: '.fire-resistant',
      middle: '.middle-glass',
      outside: '.outside-glass',
    };

    const lastValues = { frg: '', middle: '', outside: '' };

    setInterval(() => {
      Object.entries(selectors).forEach(([key, sel]) => {
        const select = document.querySelector(sel);
        if (!select) return;

        const currentText = select.options[select.selectedIndex]?.text || '—';
        if (currentText !== lastValues[key]) {
          lastValues[key] = currentText;
          const display = this.glassDisplays[key];
          if (display) {
            let text = currentText === '-' ? '—' : currentText;
            // Strip "Brandschutzglas" prefix for frg chip (label already says it)
            if (key === 'frg') text = text.replace(/^Brandschutzglas\s*/i, '');
            display.textContent = text;
          }
        }
      });

      // Update weight, spacer, gas, price from GlassStructure
      this._updateStructureInfo();
    }, 200);
  }

  _updateStructureInfo() {
    const structure = window._cdrStructure;
    if (!structure) return;

    // Weight from fire-resistant glass
    if (structure.fireResistentGlass) {
      const w = structure.fireResistentGlass.weight;
      const wText = isNaN(w) ? '—' : w.toFixed(1);
      if (this.weightDisplay) this.weightDisplay.textContent = wText;
      if (this.mobileWeight) this.mobileWeight.textContent = wText;

      // Element thickness = sum of all glass panes + spacers
      let totalThickness = structure.fireResistentGlass.thickness || 0;
      if (structure.spacerOutside?.thickness) totalThickness += structure.spacerOutside.thickness;
      if (structure.outerGlass?.thickness) totalThickness += structure.outerGlass.thickness;
      if (structure.spacerMiddle?.thickness) totalThickness += structure.spacerMiddle.thickness;
      if (structure.middleGlass?.thickness) totalThickness += structure.middleGlass.thickness;
      if (this.thicknessDisplay) {
        this.thicknessDisplay.textContent = totalThickness > 0 ? totalThickness.toFixed(1) : '—';
      }
    }

    // Spacer
    if (this.spacerDisplay) {
      const s1 = structure.spacerOutside?.thickness;
      const s2 = structure.spacerMiddle?.thickness;
      if (s1 && s2 && s2 !== 0) {
        this.spacerDisplay.textContent = s1 + ' / ' + s2 + ' mm';
      } else if (s1) {
        this.spacerDisplay.textContent = s1 + ' mm';
      } else {
        this.spacerDisplay.textContent = '—';
      }
    }

    // Gas
    if (this.gasDisplay) {
      const g1 = structure.gasOutside;
      const g2 = structure.gasMiddle;
      if (g1 && g2 && g2 !== 0) {
        this.gasDisplay.textContent = g1 + ' / ' + g2;
      } else if (g1) {
        this.gasDisplay.textContent = String(g1);
      } else {
        this.gasDisplay.textContent = '—';
      }
    }

    // Price from PricingEngine
    this._updatePrice(structure);
  }

  _updatePrice(structure) {
    const engine = window._cdrPricingEngine;
    if (!engine || !engine.ready || !this.priceDisplay) return;

    const frg = structure.fireResistentGlass;
    if (!frg) return;

    const frgId = parseInt(frg.id);
    const structureType = frg.structure || 'mono';
    const gas = structure.gasOutside || 'Argon';

    // Get outside glass ID
    const outGlass = structure.outerGlass;
    const outId = outGlass ? parseInt(outGlass.id) : null;

    // Get middle glass ID
    const midGlass = structure.middleGlass;
    const midId = midGlass ? parseInt(midGlass.id) : null;

    const price = engine.getPrice(frgId, structureType, outId, midId, gas === 0 ? 'Argon' : String(gas));

    if (price !== null) {
      this.priceDisplay.textContent = price.toFixed(2);
    } else {
      this.priceDisplay.textContent = '—';
    }
  }
}
